import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Zap, Building2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function RecentActivity() {
  const { data: activities = [] } = useQuery<any[]>({
    queryKey: ["/api/activities"],
    staleTime: 0, // Mark data as stale immediately
    refetchOnMount: true, // Refetch when component mounts
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'file_upload':
        return <FileText className="w-4 h-4 text-green-600" />;
      case 'vbr_generated':
        return <Zap className="w-4 h-4 text-purple-600" />;
      case 'station_created':
        return <Building2 className="w-4 h-4 text-blue-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActivityBgColor = (type: string) => {
    switch (type) {
      case 'file_upload':
        return 'bg-green-100';
      case 'vbr_generated':
        return 'bg-purple-100';
      case 'station_created':
        return 'bg-blue-100';
      default:
        return 'bg-gray-100';
    }
  };

  return (
    <Card className="mt-8">
      <CardHeader>
        <CardTitle className="text-xl font-semibold text-gray-900">Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8" data-testid="text-no-activities">
            <p className="text-gray-500">No recent activity</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activities.map((activity: any) => (
              <div 
                key={activity.id} 
                className="flex items-start space-x-4 pb-4 border-b border-gray-100 last:border-b-0"
                data-testid={`activity-${activity.type}`}
              >
                <div className={`p-2 rounded-lg ${getActivityBgColor(activity.type)}`}>
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900">{activity.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-gray-500 text-sm" data-testid={`activity-user-${activity.id}`}>
                      {activity.userName || activity.userEmail}
                    </p>
                    <span className="text-gray-400">•</span>
                    <p className="text-gray-500 text-sm" data-testid={`activity-time-${activity.id}`}>
                      {formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {activities.length > 0 && (
          <div className="mt-6">
            <Button variant="ghost" size="sm" className="text-primary-600 hover:text-primary-700">
              View all activity →
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
